import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Settings, TktInstances } from '../../services';
import { reset_client_get_caches } from '../../services/Client';
import { updateUser } from '../../redux/modules/users';
import { applySetting } from '../../redux/modules/settings';
import { setSelectedEditionId } from '../../redux/modules/editionNavigation';
import {
    parseUrlContext,
    buildContextUrl,
    editionSegmentFor,
} from '../../routing/contextUrl';
import { Loader } from '../../components';
import DeeplinkAccessDenied from '../DeeplinkAccessDenied/DeeplinkAccessDenied';

/**
 * Reconciles the instance/edition encoded in the URL with the authenticated
 * session before rendering the app.
 *
 * Behaviour:
 *  - Bare/legacy URLs (no `/i/:slug` prefix) are rewritten in place to the
 *    canonical contextual URL (one redirect, so the router basename gets set).
 *  - A URL pointing at another instance triggers an instance switch when the
 *    user has access, or an explicit "access denied" screen otherwise.
 *  - The edition is driven by the URL: `/e/:id` selects that edition, no
 *    edition segment means the instance's active edition.
 *
 * Basename is fixed at boot from the URL. When the deep link already carries
 * the target `/i/:slug`, we finish the instance switch in-place (hydrate
 * settings, no document reload). Only a basename/path change forces
 * location.replace. After switch we also strip a redundant `/e/:active` in
 * that same navigation when needed.
 */
const STATUS_PENDING = 'pending';
const STATUS_READY = 'ready';
const STATUS_DENIED = 'denied';

const editionDocId = (e) => String(e?._id || e?.id || '');

const ContextGate = ({ children }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const setting = useSelector((state) => state.settings.setting);
    const isLoadingSettings = useSelector((state) => state.settings.isLoading);
    const settingsLoadFailed = useSelector((state) => state.settings.hasLoadingError);
    const selectedEditionId = useSelector((state) => state.editionNavigation?.selectedEditionId);
    const editions = useSelector((state) => state.editionNavigation?.editions);

    const [status, setStatus] = useState(STATUS_PENDING);
    const [deniedReason, setDeniedReason] = useState(null);

    // Guards a single irreversible side-effect (reload / instance switch) per mount.
    const sideEffectStartedRef = useRef(false);

    useEffect(() => {
        if (sideEffectStartedRef.current) {
            return;
        }
        if (!user || !user.id) {
            // auth bootstrap still in flight
            return;
        }

        const urlCtx = parseUrlContext(window.location.pathname);
        const search = window.location.search || '';
        const hash = window.location.hash || '';
        const currentSlug = user.tkt_instance?.slug || null;
        const activeEdition = setting?.edition ? String(setting.edition) : null;
        const isSuperAdmin = typeof user.isSuperAdmin === 'function' && user.isSuperAdmin();

        // ---- Case A: no instance prefix in the URL → rewrite to canonical ----
        if (!urlCtx.slug) {
            if (!currentSlug) {
                // Instance without a slug: cannot build a prefix, run unprefixed.
                setStatus(STATUS_READY);
                return;
            }
            const editionSeg = editionSegmentFor(selectedEditionId, activeEdition);
            const target = buildContextUrl(
                { slug: currentSlug, editionId: editionSeg },
                urlCtx.logicalPath,
                search,
                hash
            );
            sideEffectStartedRef.current = true;
            window.location.replace(target);
            return;
        }

        // ---- Case B1: URL targets another instance → switch or deny ----
        if (currentSlug && urlCtx.slug !== currentSlug) {
            sideEffectStartedRef.current = true;

            const switch_to = (instanceId) => {
                dispatch(
                    updateUser(
                        { id: user.id, tkt_instance_id: instanceId },
                        { switching_tkt_instance: true }
                    )
                )
                    .then(() => (
                        Settings.get('default')
                            .then((data) => {
                                // Session now targets urlCtx.slug. Prefer staying on this
                                // document when the URL basename already matches; only
                                // replace when /e/:active must be stripped (or path differs).
                                const targetActive = data?.setting?.edition != null
                                    ? String(data.setting.edition)
                                    : null;
                                const editionId = (
                                    urlCtx.editionId
                                    && targetActive
                                    && String(urlCtx.editionId) === targetActive
                                )
                                    ? null
                                    : urlCtx.editionId;
                                const target = buildContextUrl(
                                    { slug: urlCtx.slug, editionId },
                                    urlCtx.logicalPath,
                                    search,
                                    hash
                                );
                                const current = `${window.location.pathname}${search}${hash}`;
                                if (target !== current) {
                                    window.location.replace(target);
                                    return;
                                }
                                reset_client_get_caches();
                                if (data?.setting) {
                                    dispatch(applySetting(data.setting));
                                }
                                dispatch(setSelectedEditionId(editionId || targetActive || null));
                                setStatus(STATUS_READY);
                            })
                            .catch(() => {
                                // Switch already applied; fall back to a single reload.
                                window.location.reload();
                            })
                    ))
                    .catch(() => {
                        sideEffectStartedRef.current = false;
                        setDeniedReason('instance');
                        setStatus(STATUS_DENIED);
                    });
            };

            if (isSuperAdmin) {
                TktInstances.infos(urlCtx.slug)
                    .then((json) => {
                        const id = json?.infos?.id;
                        if (!id) {
                            throw new Error('missing id');
                        }
                        switch_to(id);
                    })
                    .catch(() => {
                        sideEffectStartedRef.current = false;
                        setDeniedReason('instance');
                        setStatus(STATUS_DENIED);
                    });
                return;
            }

            TktInstances.infos(urlCtx.slug)
                .then((json) => {
                    const id = json?.infos?.id;
                    const memberships = Array.isArray(user.tkt_instance_ids)
                        ? user.tkt_instance_ids.map(String)
                        : [];
                    if (id && memberships.includes(String(id))) {
                        switch_to(id);
                        return;
                    }
                    sideEffectStartedRef.current = false;
                    setDeniedReason('instance');
                    setStatus(STATUS_DENIED);
                })
                .catch(() => {
                    sideEffectStartedRef.current = false;
                    setDeniedReason('instance');
                    setStatus(STATUS_DENIED);
                });
            return;
        }

        // ---- Case B2: correct instance → reconcile edition ----
        if (urlCtx.editionId) {
            const loadedEditions = Array.isArray(editions) ? editions : [];

            // Canonicalize: an explicit edition equal to the active one is redundant.
            if (activeEdition && urlCtx.editionId === activeEdition) {
                sideEffectStartedRef.current = true;
                window.location.replace(
                    buildContextUrl({ slug: currentSlug, editionId: null }, urlCtx.logicalPath, search, hash)
                );
                return;
            }

            // Validate only when the list is actually available.
            if (loadedEditions.length > 0) {
                const known = loadedEditions.some((e) => editionDocId(e) === String(urlCtx.editionId));
                if (!known) {
                    setDeniedReason('edition');
                    setStatus(STATUS_DENIED);
                    return;
                }
            }

            if (String(selectedEditionId || '') !== String(urlCtx.editionId)) {
                dispatch(setSelectedEditionId(urlCtx.editionId));
            }
            setStatus(STATUS_READY);
            return;
        }

        // No edition segment → instance's active edition (URL is source of truth).
        // Wait until settings finished loading: initial state is setting:{} with
        // isLoading false, so "no edition yet" looked like "instance has none"
        // and cleared selectedEditionId (prefs) → bare monoid GETs on F5.
        if (settingsLoadFailed) {
            setStatus(STATUS_READY);
            return;
        }
        if (isLoadingSettings || !setting?.customer) {
            return;
        }
        if (!activeEdition) {
            if (selectedEditionId) {
                dispatch(setSelectedEditionId(null));
            }
        } else if (String(selectedEditionId || '') !== activeEdition) {
            dispatch(setSelectedEditionId(activeEdition));
        }
        setStatus(STATUS_READY);
    }, [
        user,
        setting,
        isLoadingSettings,
        settingsLoadFailed,
        selectedEditionId,
        editions,
        dispatch,
    ]);

    if (status === STATUS_DENIED) {
        const urlCtx = parseUrlContext(window.location.pathname);
        return (
            <DeeplinkAccessDenied
                reason={deniedReason}
                slug={urlCtx.slug}
                editionId={urlCtx.editionId}
                currentInstanceName={user?.tkt_instance?.name}
                currentSlug={user?.tkt_instance?.slug}
            />
        );
    }

    if (status === STATUS_PENDING) {
        return (
            <div className="container-fluid loader-container justify-content-center">
                <Loader />
            </div>
        );
    }

    return <>{children}</>;
};

export default ContextGate;
